/**
 * Black Friday 2025 - iCart Socks Size Selector Form
 *
 * Displays a size selector form in the iCart for free socks (3rd tier gift).
 * Depends on: window.BF2025CartProgress API from black-friday-2025-cart-manager.js
 * Depends on: window.BF_SOCKS_FORM_HTML template from black-friday-2025.liquid
 */

(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    icartList: '.icart-main .icart-list',
    socksForm: '.bf-socks-form',
    sizeBtn: '.bf-socks-size-btn'
  };

  let observer = null;
  let currentSocksForm = null;
  let isProcessing = false;

  /**
   * Check if socks form should be shown
   */
  function shouldShowForm() {
    if (!window.BF2025CartProgress || typeof window.BF2025CartProgress.shouldShowSocksForm !== 'function') {
      console.warn('[BF Socks Form] Cart manager not initialized');
      return false;
    }
    return window.BF2025CartProgress.shouldShowSocksForm();
  }

  /**
   * Show the socks form
   */
  function showForm() {
    if (currentSocksForm) {
      currentSocksForm.classList.remove('bf-2025--hidden');
      console.log('[BF Socks Form] Form shown');
    }
  }

  /**
   * Hide the socks form
   */
  function hideForm() {
    if (currentSocksForm) {
      currentSocksForm.classList.add('bf-2025--hidden');
      console.log('[BF Socks Form] Form hidden');
    }
  }

  /**
   * Add sock variant to cart
   */
  async function addSocksToCart(variantId, size) {
    if (isProcessing) {
      console.warn('[BF Socks Form] Already processing, please wait');
      return;
    }

    console.log(`[BF Socks Form] Adding socks size ${size} (variant ${variantId}) to cart`);
    isProcessing = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: String(variantId),
            quantity: 1,
            properties: {
              _bf_free_gift: 'true'
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add socks: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[BF Socks Form] ✅ Socks added successfully:', result);

      // Hide form after successful addition
      hideForm();

      // Refresh iCart UI
      if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
        console.log('[BF Socks Form] Refreshing iCart UI');
        window.prepareDataForDisplayWidgetByRule();
      }

    } catch (error) {
      console.error('[BF Socks Form] Failed to add socks:', error);
      alert('Failed to add socks to cart. Please try again.');
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Handle size button click
   */
  function handleSizeButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const variantId = button.getAttribute('data-variant-id');
    const size = button.getAttribute('data-size');

    if (!variantId) {
      console.error('[BF Socks Form] No variant ID found on button');
      return;
    }

    addSocksToCart(variantId, size);
  }

  /**
   * Create and inject the socks form into iCart
   */
  function injectSocksForm() {
    const icartList = document.querySelector(SELECTORS.icartList);
    if (!icartList) {
      console.warn('[BF Socks Form] .icart-list not found');
      return;
    }

    // Check if we already have a socks form
    const existingForm = icartList.querySelector(SELECTORS.socksForm);
    if (existingForm) {
      currentSocksForm = existingForm;
      return;
    }

    // Check if template HTML is available
    if (!window.BF_SOCKS_FORM_HTML) {
      console.warn('[BF Socks Form] Template HTML not found in window.BF_SOCKS_FORM_HTML');
      return;
    }

    // Create form from template
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = window.BF_SOCKS_FORM_HTML.trim();
    const socksForm = tempDiv.firstChild;

    if (!socksForm) {
      console.warn('[BF Socks Form] Failed to create socks form element');
      return;
    }

    // Find the progress bar and insert form after it
    const progressBar = icartList.querySelector('.bf-progress-bar');
    if (progressBar && progressBar.nextSibling) {
      icartList.insertBefore(socksForm, progressBar.nextSibling);
    } else {
      // Fallback: insert as second child (after progress bar, which is first)
      if (icartList.children.length > 1) {
        icartList.insertBefore(socksForm, icartList.children[1]);
      } else {
        icartList.appendChild(socksForm);
      }
    }

    currentSocksForm = socksForm;

    // Attach event listeners to size buttons
    const sizeButtons = socksForm.querySelectorAll(SELECTORS.sizeBtn);
    sizeButtons.forEach(button => {
      button.addEventListener('click', handleSizeButtonClick);
    });

    console.log('[BF Socks Form] Form injected into iCart with', sizeButtons.length, 'size buttons');
  }

  /**
   * Remove the socks form from iCart
   */
  function removeSocksForm() {
    if (currentSocksForm && currentSocksForm.parentNode) {
      currentSocksForm.parentNode.removeChild(currentSocksForm);
      currentSocksForm = null;
      console.log('[BF Socks Form] Form removed');
    }
  }

  /**
   * Update form visibility based on cart state
   */
  function updateFormVisibility() {
    const shouldShow = shouldShowForm();

    if (shouldShow) {
      // Ensure form exists
      if (!currentSocksForm) {
        injectSocksForm();
      }

      // Show the form
      if (currentSocksForm) {
        showForm();
      }
    } else {
      // Hide the form
      hideForm();
    }
  }

  /**
   * Wait for an element to appear in the DOM using MutationObserver
   */
  function waitForElement(selector, callback) {
    const element = document.querySelector(selector);
    if (element) {
      callback(element);
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const element = document.querySelector(selector);
      if (element) {
        obs.disconnect();
        callback(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize MutationObserver to watch for cart changes
   */
  function initObserver() {
    const icartItems = document.querySelector('.icart-main .icart-items');
    if (!icartItems) {
      console.log('[BF Socks Form] .icart-items not found, waiting for it to appear...');
      return false;
    }

    // Create observer to watch cart items
    observer = new MutationObserver(() => {
      updateFormVisibility();
    });

    observer.observe(icartItems, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log('[BF Socks Form] MutationObserver initialized');

    // Initial update
    updateFormVisibility();

    return true;
  }

  /**
   * Initialize the socks form system
   */
  function init() {
    console.log('[BF Socks Form] Initializing...');

    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Check for dependencies
    if (!window.BF2025CartProgress) {
      console.warn('[BF Socks Form] Waiting for cart manager...');
      setTimeout(init, 500);
      return;
    }

    if (!window.BF_SOCKS_FORM_HTML) {
      console.warn('[BF Socks Form] Waiting for template HTML...');
      setTimeout(init, 500);
      return;
    }

    // Try to initialize observer
    const success = initObserver();
    if (!success) {
      // Wait for iCart to appear using MutationObserver (reactive, no polling)
      waitForElement('.icart-main .icart-items', () => {
        console.log('[BF Socks Form] .icart-items appeared, initializing observer');
        initObserver();
      });
    }
  }

  // Start initialization
  init();

  // Also listen for custom cart update events
  if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, () => {
      updateFormVisibility();
    });
  }

  // Expose refresh function for manual updates if needed
  window.BF2025SocksForm = {
    refresh: updateFormVisibility
  };

})();
